import { useEffect, useRef } from 'react';
import * as THREE from 'three';

const ParticleSwarm = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const scene = new THREE.Scene();
    const W = window.innerWidth, H = window.innerHeight;
    const camera = new THREE.PerspectiveCamera(65, W / H, 0.1, 1000);
    camera.position.z = 20;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));

    const isMobile = W < 768;
    const COUNT = isMobile ? 800 : 2000;

    const positions = new Float32Array(COUNT * 3);
    const origPos = new Float32Array(COUNT * 3);
    const colors = new Float32Array(COUNT * 3);
    const phases = new Float32Array(COUNT);
    const speeds = new Float32Array(COUNT);

    // Brand colors: turquoise #30d5c8 | green #55d532
    const c1 = new THREE.Color('#30d5c8'); // turquoise
    const c2 = new THREE.Color('#55d532'); // green
    const c3 = new THREE.Color('#4fe8ba'); // mid green-turquoise

    for (let i = 0; i < COUNT; i++) {
      const r = 4 + Math.random() * 14;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const x = r * Math.sin(phi) * Math.cos(theta);
      const y = r * Math.sin(phi) * Math.sin(theta);
      const z = r * Math.cos(phi);

      positions[i * 3] = origPos[i * 3] = x;
      positions[i * 3 + 1] = origPos[i * 3 + 1] = y;
      positions[i * 3 + 2] = origPos[i * 3 + 2] = z;

      const rnd = Math.random();
      const c = rnd < 0.5 ? c1 : rnd < 0.8 ? c2 : c3;
      colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b;

      phases[i] = Math.random() * Math.PI * 2;
      speeds[i] = 0.2 + Math.random() * 0.8;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const mat = new THREE.PointsMaterial({ size: 0.07, vertexColors: true, transparent: true, opacity: 0.82, sizeAttenuation: true });
    const points = new THREE.Points(geo, mat);
    scene.add(points);

    const linePairs = 80;
    const linePosArr = new Float32Array(linePairs * 6);
    for (let i = 0; i < linePairs; i++) {
      const a = Math.floor(Math.random() * COUNT);
      const b = Math.floor(Math.random() * COUNT);
      linePosArr[i * 6] = origPos[a * 3]; linePosArr[i * 6 + 1] = origPos[a * 3 + 1]; linePosArr[i * 6 + 2] = origPos[a * 3 + 2];
      linePosArr[i * 6 + 3] = origPos[b * 3]; linePosArr[i * 6 + 4] = origPos[b * 3 + 1]; linePosArr[i * 6 + 5] = origPos[b * 3 + 2];
    }
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.BufferAttribute(linePosArr, 3));
    const lineMat = new THREE.LineBasicMaterial({ color: '#30d5c8', transparent: true, opacity: 0.09 });
    const lines = new THREE.LineSegments(lineGeo, lineMat);
    scene.add(lines);

    let t = 0;
    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      t += 0.0025;
      const pa = geo.attributes.position.array;
      for (let i = 0; i < COUNT; i++) {
        const ph = phases[i], sp = speeds[i], amp = 0.22;
        pa[i * 3] = origPos[i * 3] + Math.sin(t * sp + ph) * amp;
        pa[i * 3 + 1] = origPos[i * 3 + 1] + Math.cos(t * sp * 1.1 + ph * 1.3) * amp;
        pa[i * 3 + 2] = origPos[i * 3 + 2] + Math.sin(t * sp * 0.9 + ph * 0.7) * amp;
      }
      geo.attributes.position.needsUpdate = true;
      points.rotation.y = t * 0.08;
      points.rotation.x = Math.sin(t * 0.04) * 0.12;
      lines.rotation.y = t * 0.08;
      lines.rotation.x = Math.sin(t * 0.04) * 0.12;
      camera.position.x += (mouseRef.current.x * 2.5 - camera.position.x) * 0.02;
      camera.position.y += (-mouseRef.current.y * 2.5 - camera.position.y) * 0.02;
      camera.lookAt(scene.position);
      renderer.render(scene, camera);
    };
    animate();

    const onMouseMove = (e) => { mouseRef.current.x = (e.clientX / window.innerWidth) * 2 - 1; mouseRef.current.y = (e.clientY / window.innerHeight) * 2 - 1; };
    const onResize = () => { camera.aspect = window.innerWidth / window.innerHeight; camera.updateProjectionMatrix(); renderer.setSize(window.innerWidth, window.innerHeight); };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', onResize);
      renderer.dispose(); geo.dispose(); mat.dispose(); lineGeo.dispose(); lineMat.dispose();
    };
  }, []);

  return (
    <canvas ref={canvasRef} data-testid="particle-swarm-canvas"
      style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2, pointerEvents: 'none' }} />
  );
};

export default ParticleSwarm;
